import { negotiateVideoMime } from '../utils/mimeNegotiate';

export interface RecordVideoOptions {
  canvas: HTMLCanvasElement;
  audioTrack?: MediaStreamTrack | null;
  maxSeconds: number;
  fps?: number;
  onProgress?: (elapsedMs: number) => void;
  signal?: AbortSignal;
}

export interface RecordedVideo {
  blob: Blob;
  mimeType: string;
  durationMs: number;
}

export async function recordVideo(
  opts: RecordVideoOptions,
): Promise<RecordedVideo> {
  const { canvas, audioTrack, maxSeconds, fps = 30, onProgress, signal } = opts;
  if (typeof MediaRecorder === 'undefined') {
    throw new Error('MediaRecorder not supported in this browser');
  }
  const mime = negotiateVideoMime();
  if (!mime) {
    throw new Error('No supported video mime type for MediaRecorder');
  }

  const stream = canvas.captureStream(fps);
  if (audioTrack) {
    stream.addTrack(audioTrack);
  }

  const recorder = new MediaRecorder(stream, { mimeType: mime.mime });
  const chunks: Blob[] = [];
  const start = performance.now();

  return await new Promise<RecordedVideo>((resolve, reject) => {
    let timer: number | undefined;
    let progressTimer: number | undefined;
    let stopped = false;

    const cleanup = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      if (progressTimer !== undefined) window.clearInterval(progressTimer);
      stream.getTracks().forEach((t) => {
        // Stop only the canvas-derived video tracks; leave the camera's
        // audio track alive (it's owned by useCamera and will be reused).
        if (t.kind === 'video') t.stop();
      });
      if (signal) signal.removeEventListener('abort', onAbort);
    };

    const onAbort = () => {
      if (stopped) return;
      stopped = true;
      try {
        recorder.stop();
      } catch {
        /* ignore */
      }
    };

    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunks.push(ev.data);
    };
    recorder.onerror = (ev) => {
      cleanup();
      reject(new Error((ev as ErrorEvent).message || 'MediaRecorder error'));
    };
    recorder.onstop = () => {
      cleanup();
      const blob = new Blob(chunks, { type: mime.mime });
      resolve({
        blob,
        mimeType: mime.mime,
        durationMs: performance.now() - start,
      });
    };

    try {
      recorder.start(250);
    } catch (err) {
      cleanup();
      reject(err);
      return;
    }

    timer = window.setTimeout(() => {
      if (stopped) return;
      stopped = true;
      try {
        recorder.stop();
      } catch {
        /* ignore */
      }
    }, maxSeconds * 1000);

    if (onProgress) {
      progressTimer = window.setInterval(() => {
        onProgress(performance.now() - start);
      }, 100);
    }

    if (signal) {
      if (signal.aborted) onAbort();
      else signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}
